import { AlertCircle, Briefcase, Link as LinkIcon, RefreshCw, Check, X, ShoppingBag, Store } from 'lucide-react';
import React, { useCallback, useEffect, useState } from 'react';
import { useChatbot } from '../../contexts/ChatbotContext';
import { useDesignColors } from '../../hooks/useDesignColors';
import { useProductIntegrations } from '../../hooks/useProductIntegrations';

interface IntegrationFormProps {
  type: 'shopify' | 'prestashop';
  title: string;
  icon: React.ReactNode;
  config: any;
  isActive: boolean;
  onSave: (config: any, isActive: boolean) => Promise<void>;
  onCancel: () => void;
  loading: boolean;
}

interface ShopifyFormConfig {
  shopDomain: string;
  accessToken: string;
  [key: string]: string;
}

interface PrestaShopFormConfig {
  shopUrl: string;
  apiKey: string;
  [key: string]: string;
}

type FormConfig = ShopifyFormConfig | PrestaShopFormConfig;

function IntegrationForm({ 
  type, 
  title, 
  icon, 
  config, 
  isActive, 
  onSave, 
  onCancel,
  loading 
}: IntegrationFormProps) {
  const [formConfig, setFormConfig] = useState<FormConfig>(config);
  const [formActive, setFormActive] = useState(isActive);
  const { colors } = useDesignColors();
  const { chatbotPublicId } = useChatbot();
  
  // URL du webhook avec l'URL netlify/functions directe
  const webhookUrl = `https://api.botmaker-agency.ai/.netlify/functions/product-webhook?provider=${type}&chatbot_id=${chatbotPublicId}`;
  const [webhookCopied, setWebhookCopied] = useState(false);

  const handleInputChange = (field: string, value: string) => {
    setFormConfig(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await onSave(formConfig, formActive);
  };
  
  const copyWebhookUrl = () => {
    navigator.clipboard.writeText(webhookUrl);
    setWebhookCopied(true);
    setTimeout(() => setWebhookCopied(false), 2000);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          {icon}
          <h3 className="text-lg font-semibold text-white">{title}</h3>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-400 mr-2">Active</span>
          <button
            type="button"
            onClick={() => setFormActive(!formActive)}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
              formActive ? 'bg-indigo-500' : 'bg-gray-700'
            }`}
          >
            <span
              className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                formActive ? 'translate-x-6' : 'translate-x-1'
              }`}
            />
          </button>
        </div>
      </div>

      {type === 'shopify' && (
        <>
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Shop Domain
            </label>
            <input
              type="text"
              value={formConfig.shopDomain || ''}
              onChange={(e) => handleInputChange('shopDomain', e.target.value)}
              placeholder="your-store.myshopify.com"
              className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
            <p className="mt-1 text-xs text-gray-500">
              Enter your Shopify store domain without https:// or any path
            </p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Access Token
            </label>
            <input
              type="password"
              value={formConfig.accessToken || ''}
              onChange={(e) => handleInputChange('accessToken', e.target.value)}
              placeholder="shpat_..."
              className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
            <p className="mt-1 text-xs text-gray-500">
              Create a custom app in your Shopify admin to get an access token
            </p>
          </div>
          
          {/* Nouvelle section Webhook URL */}
          <div className="mt-6 p-4 bg-indigo-500/10 rounded-lg">
            <h4 className="text-sm font-medium text-indigo-400 mb-2">Webhook URL</h4>
            <div className="flex items-center">
              <input
                type="text"
                value={webhookUrl}
                readOnly
                className="flex-1 bg-black/20 border border-indigo-500/20 rounded-l-lg px-3 py-2 text-white text-xs focus:outline-none"
              />
              <button
                type="button"
                onClick={copyWebhookUrl}
                className="bg-indigo-600 hover:bg-indigo-700 text-white px-3 py-2 rounded-r-lg text-xs"
              >
                {webhookCopied ? 'Copied!' : 'Copy'}
              </button>
            </div>
            <p className="mt-2 text-xs text-gray-400">
              When you activate the integration, webhooks are automatically registered for your store. If you prefer to configure them manually, use this URL in your Shopify webhook settings.
            </p>
          </div>
        </>
      )}

      {type === 'prestashop' && (
        <>
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Shop URL
            </label>
            <input
              type="text"
              value={formConfig.shopUrl || ''}
              onChange={(e) => handleInputChange('shopUrl', e.target.value)}
              placeholder="https://your-store.com"
              className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
            <p className="mt-1 text-xs text-gray-500">
              Enter your PrestaShop store URL including https://
            </p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              API Key
            </label>
            <input
              type="password"
              value={formConfig.apiKey || ''}
              onChange={(e) => handleInputChange('apiKey', e.target.value)}
              placeholder="Your PrestaShop API key"
              className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
            <p className="mt-1 text-xs text-gray-500">
              Generate an API key in your PrestaShop admin under Advanced Parameters &amp; Webservice
            </p>
          </div>
          
          {/* Nouvelle section Webhook URL pour PrestaShop aussi */}
          <div className="mt-6 p-4 bg-indigo-500/10 rounded-lg">
            <h4 className="text-sm font-medium text-indigo-400 mb-2">Webhook URL</h4>
            <div className="flex items-center">
              <input
                type="text"
                value={webhookUrl}
                readOnly
                className="flex-1 bg-black/20 border border-indigo-500/20 rounded-l-lg px-3 py-2 text-white text-xs focus:outline-none"
              />
              <button
                type="button"
                onClick={copyWebhookUrl}
                className="bg-indigo-600 hover:bg-indigo-700 text-white px-3 py-2 rounded-r-lg text-xs"
              >
                {webhookCopied ? 'Copied!' : 'Copy'}
              </button>
            </div>
            <p className="mt-2 text-xs text-gray-400">
              For PrestaShop, you'll need to install a webhook module and configure this URL manually. Please refer to the documentation below for more information.
            </p>
          </div>
        </>
      )}

      <div className="flex justify-end gap-3 pt-4">
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 rounded-lg bg-white/5 text-gray-300 hover:bg-white/10 transition-colors"
          disabled={loading}
        >
          <X className="w-4 h-4 inline mr-2" />
          Cancel
        </button>
        <button
          type="submit"
          className="px-4 py-2 rounded-lg text-white transition-all flex items-center gap-2"
          style={{
            background: `linear-gradient(135deg, ${colors.gradientStart}, ${colors.gradientEnd})`,
          }}
          disabled={loading}
        >
          {loading ? (
            <>
              <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
              Saving...
            </>
          ) : (
            <>
              <Check className="w-4 h-4" />
              Save
            </>
          )}
        </button>
      </div>
    </form>
  );
}

interface IntegrationSectionProps {
  type: 'shopify' | 'prestashop';
  title: string;
  icon: React.ReactNode;
  config: any;
  isActive: boolean;
  onEdit: () => void;
  onSync: () => Promise<void>;
  loading: boolean;
  syncLoading: boolean;
}

function IntegrationSection({ 
  type, 
  title, 
  icon, 
  config, 
  isActive, 
  onEdit, 
  onSync,
  loading,
  syncLoading
}: IntegrationSectionProps) {
  return (
    <div className="bg-white/5 backdrop-blur-xl rounded-xl border border-white/10">
      <div className="p-6 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-indigo-500/20 flex items-center justify-center">
            {icon}
          </div>
          <div className="text-left">
            <h3 className="text-lg font-semibold text-white">{title}</h3>
            <p className="text-sm text-gray-400">
              {isActive 
                ? `Connected to ${type === 'shopify' ? config.shopDomain : config.shopUrl}` 
                : `Synchronize your products from ${title}`}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-4">
          {isActive && (
            <button
              onClick={onSync}
              disabled={syncLoading}
              className="px-4 py-2 rounded-lg bg-indigo-500/20 text-indigo-400 hover:bg-indigo-500/30 transition-colors flex items-center gap-2"
            >
              {syncLoading ? (
                <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
              ) : (
                <RefreshCw className="w-4 h-4" />
              )}
              Sync Products
            </button>
          )}
          <button
            onClick={onEdit}
            disabled={loading}
            className="px-4 py-2 rounded-lg bg-white/5 text-gray-300 hover:bg-white/10 transition-colors"
          >
            {isActive ? 'Edit' : 'Configure'}
          </button>
        </div>
      </div>
    </div>
  );
}

export function ProductIntegrations() {
  const { chatbotPublicId } = useChatbot();
  const { colors } = useDesignColors();
  const { 
    config, 
    loading, 
    error, 
    initialized,
    initializeServices,
    updateShopifyConfig,
    updatePrestaShopConfig,
    syncProducts
  } = useProductIntegrations();
  
  const [editingIntegration, setEditingIntegration] = useState<'shopify' | 'prestashop' | null>(null);
  const [syncingIntegration, setSyncingIntegration] = useState<'shopify' | 'prestashop' | null>(null);

  useEffect(() => {
    if (chatbotPublicId && !initialized) {
      initializeServices(chatbotPublicId);
    }
  }, [chatbotPublicId, initialized, initializeServices]);

  const handleEditIntegration = (type: 'shopify' | 'prestashop') => {
    setEditingIntegration(type);
  };

  const handleSaveShopify = async (newConfig: any, isActive: boolean) => {
    if (!chatbotPublicId) return;
    
    try {
      await updateShopifyConfig({
        enabled: isActive,
        config: newConfig
      }, chatbotPublicId);
      
      setEditingIntegration(null);
    } catch (err) {
      console.error('Error saving Shopify config:', err);
    }
  };

  const handleSavePrestaShop = async (newConfig: any, isActive: boolean) => {
    if (!chatbotPublicId) return;
    
    try {
      await updatePrestaShopConfig({
        enabled: isActive,
        config: newConfig
      }, chatbotPublicId);
      
      setEditingIntegration(null);
    } catch (err) {
      console.error('Error saving PrestaShop config:', err);
    }
  };

  const handleSyncProducts = async (type: 'shopify' | 'prestashop') => {
    try {
      setSyncingIntegration(type);
      await syncProducts(type);
    } catch (err) {
      console.error(`Error syncing ${type} products:`, err);
    } finally {
      setSyncingIntegration(null);
    }
  };

  if (loading && !initialized) {
    return (
      <div className="flex justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-indigo-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {error && (
        <div className="bg-red-500/10 text-red-400 p-4 rounded-lg flex items-center gap-2">
          <AlertCircle className="w-5 h-5" />
          <p>{error}</p>
        </div>
      )}

      {editingIntegration === 'shopify' ? (
        <div className="bg-white/5 backdrop-blur-xl rounded-xl p-6 border border-white/10">
          <IntegrationForm
            type="shopify"
            title="Shopify"
            icon={<ShoppingBag className="w-5 h-5 text-indigo-400" />}
            config={config.shopify?.config || { shopDomain: '', accessToken: '' }}
            isActive={config.shopify?.enabled || false}
            onSave={handleSaveShopify}
            onCancel={() => setEditingIntegration(null)}
            loading={loading}
          />
        </div>
      ) : (
        <IntegrationSection
          type="shopify"
          title="Shopify"
          icon={<ShoppingBag className="w-5 h-5 text-indigo-400" />}
          config={config.shopify?.config || {}}
          isActive={config.shopify?.enabled || false}
          onEdit={() => handleEditIntegration('shopify')}
          onSync={() => handleSyncProducts('shopify')}
          loading={loading}
          syncLoading={syncingIntegration === 'shopify'}
        />
      )}

      {editingIntegration === 'prestashop' ? (
        <div className="bg-white/5 backdrop-blur-xl rounded-xl p-6 border border-white/10">
          <IntegrationForm
            type="prestashop"
            title="PrestaShop"
            icon={<Store className="w-5 h-5 text-indigo-400" />}
            config={config.prestashop?.config || { shopUrl: '', apiKey: '' }}
            isActive={config.prestashop?.enabled || false}
            onSave={handleSavePrestaShop}
            onCancel={() => setEditingIntegration(null)}
            loading={loading}
          />
        </div>
      ) : (
        <IntegrationSection
          type="prestashop"
          title="PrestaShop"
          icon={<Store className="w-5 h-5 text-indigo-400" />}
          config={config.prestashop?.config || {}}
          isActive={config.prestashop?.enabled || false}
          onEdit={() => handleEditIntegration('prestashop')}
          onSync={() => handleSyncProducts('prestashop')}
          loading={loading}
          syncLoading={syncingIntegration === 'prestashop'}
        />
      )}

      {/* Documentation Section */}
      <div className="bg-white/5 backdrop-blur-xl rounded-xl p-6 border border-white/10">
        <h3 className="text-lg font-semibold text-white mb-4">Integration Guide</h3>
        
        <div className="space-y-6">
          <div>
            <h4 className="text-base font-medium text-indigo-400 mb-2">Shopify Integration</h4>
            <div className="bg-black/50 rounded-lg p-4 space-y-4">
              <div>
                <h5 className="text-sm font-medium text-gray-300 mb-2">1. API Configuration</h5>
                <ol className="list-decimal list-inside space-y-2 text-gray-300 ml-4">
                  <li>Log in to your Shopify admin panel</li>
                  <li>Go to Apps &gt; App and sales channel settings</li>
                  <li>Click on "Develop apps for your store"</li>
                  <li>Create a new app and give it a name (like "Chatbot Integration")</li>
                  <li>Under "Configuration", select "Admin API integration"</li>
                  <li>Configure these permissions:
                    <ul className="list-disc list-inside ml-6 mt-1">
                      <li>read_products</li>
                      <li>read_inventory</li>
                      <li>read_orders</li>
                      <li>read_customers</li>
                    </ul>
                  </li>
                  <li>Install the app in your store</li>
                  <li>Copy the access token and paste it in the form above</li>
                </ol>
              </div>

              <div>
                <h5 className="text-sm font-medium text-gray-300 mb-2">2. Webhook Configuration</h5>
                <ol className="list-decimal list-inside space-y-2 text-gray-300 ml-4">
                  <li><strong>Automatic</strong>: Webhooks are automatically set up when you activate the integration.</li>
                  <li><strong>Manual</strong>: If you prefer to configure them manually:
                    <ul className="list-disc list-inside ml-6 mt-1 space-y-1">
                      <li>In your Shopify app, go to "Webhooks"</li>
                      <li>Add a new webhook for each event</li>
                      <li>Copy the webhook URL from the configuration form above</li>
                      <li>Configure webhooks for the following events:
                        <ul className="list-circle list-inside ml-6 mt-1">
                          <li>products/create</li>
                          <li>products/update</li>
                          <li>products/delete</li>
                          <li>orders/create</li>
                          <li>orders/updated</li>
                          <li>inventory_levels/update</li>
                        </ul>
                      </li>
                      <li>Data format: JSON</li>
                      <li>Activate each webhook</li>
                    </ul>
                  </li>
                  <li><strong>Testing</strong>: After configuration, you can test the webhooks in the "Send test notification" section of Shopify.</li>
                  <li><strong>Troubleshooting</strong>: If any issues occur:
                    <ul className="list-disc list-inside ml-6 mt-1">
                      <li>Check that the webhook URL is correct</li>
                      <li>Make sure your Shopify store can connect to this URL</li>
                      <li>Contact support if you continue to experience problems</li>
                    </ul>
                  </li>
                </ol>

                <div className="bg-black/30 p-3 rounded-lg mt-3 border border-indigo-500/20">
                  <p className="text-xs text-white"><strong className="text-indigo-400">Important:</strong> A webhook is a way for Shopify to notify our system when something changes in your store (like adding a product or receiving an order). This keeps everything automatically in sync.</p>
                </div>
              </div>
            </div>
          </div>
          
          <div>
            <h4 className="text-base font-medium text-indigo-400 mb-2">PrestaShop Integration</h4>
            <div className="bg-black/50 rounded-lg p-4 space-y-4">
              <div>
                <h5 className="text-sm font-medium text-gray-300 mb-2">1. API Configuration</h5>
                <ol className="list-decimal list-inside space-y-2 text-gray-300 ml-4">
                  <li>Log in to your PrestaShop admin panel</li>
                  <li>Go to Advanced Parameters &gt; Webservice</li>
                  <li>Enable the webservice if it's not already active</li>
                  <li>Click on "Add new webservice key"</li>
                  <li>Enter a key name (like "Chatbot Integration")</li>
                  <li>Set up these permissions:
                    <ul className="list-disc list-inside ml-6 mt-1">
                      <li>Read access for Products</li>
                      <li>Read access for Categories</li>
                      <li>Read access for Images</li>
                      <li>Read access for Orders</li>
                      <li>Read access for Customers</li>
                    </ul>
                  </li>
                  <li>Save your configuration</li>
                  <li>Copy the API key and paste it in the form above along with your store URL</li>
                </ol>
              </div>

              <div>
                <h5 className="text-sm font-medium text-gray-300 mb-2">2. Webhook Configuration</h5>
                <ol className="list-decimal list-inside space-y-2 text-gray-300 ml-4">
                  <li>Install a webhook module in your PrestaShop store if you don't already have one</li>
                  <li>Configure webhooks for these events:
                    <ul className="list-disc list-inside ml-6 mt-1">
                      <li>product_add</li>
                      <li>product_update</li>
                      <li>product_delete</li>
                      <li>order_add</li>
                      <li>order_update</li>
                    </ul>
                  </li>
                  <li>Copy the webhook URL from the form above and use it in your PrestaShop webhook settings</li>
                  <li>Make sure to save your webhook configuration in PrestaShop</li>
                </ol>
              </div>

              <div className="p-3 bg-indigo-500/10 rounded-lg">
                <p className="text-sm text-indigo-400">💡 Tip: Test your integration by creating a test order in your PrestaShop store</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}